import React, { useContext, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import _get from 'lodash-es/get';
import _noop from 'lodash-es/noop';
import Layout from 'sushiweb/Layout/Layout';
import ChipsWrapper from 'sushiweb/InputTypes/Chips';
import H6 from 'sushiweb/Typography/Heading/H6';
import { P } from 'sushiweb/Typography/Text';
import Button from 'sushiweb/Button/Button';
import Download from 'sushiweb/Icons/all/Download';
import H5 from 'sushiweb/Typography/Heading/H5';
import ChevronLeft from 'sushiweb/Icons/all/ChevronLeft';
import ChevronRight from 'sushiweb/Icons/all/ChevronRight';
import grey from 'sushiweb/tokens/color/grey';

import {
  ButtonWrapper,
  ClearFileUploadCross,
  ClearFileUploadCrossWrapper,
  ConfirmUpload,
  CreateButtonWrapper,
  CreateGroupModalFooter,
  FileSVG,
  ModalFieldsWrapper,
  ModalSuccessWrapper,
  TextWrapper,
  UnsuccessfulDownloadBtn,
  UnsuccessfulDownloadText,
  UnsuccessfulUploadText,
  UploadFileWrapper,
  UploadIconWhite,
  UploadSectionContainer,
  chipsStyle,
  clearFileBtnStyle,
  disabledUploadBtnStyle,
  downloadBtnStyle,
  uploadBtnStyle,
  uploadFileText,
} from './styles';

import { FormCacheContext } from '../../common/Components/FormTemplate/FormTemplate';
import {
  currentFlowTypeEnum,
  groupUploadErrMsg,
  groupUploadMsg,
  uploadSuccessStatus,
} from '../../../../../common/components/constants';
import { genericErrorMsg, wrongFormat } from '../../../../../../constants';
import { validateEmail } from '../../../../../../utility';
import {
  checkFileTypeSize,
  clearInputValue,
  downloadGroupCSV,
} from '../../../../../../helpers';
import { UploadsContainer } from '../../../../../common/styles';
import { UPLOAD_EMPLOYEES_URL } from '../../../../../../constants/urls';
import usePolling from '../../../../../common/utils/usePolling';
import ZTrackingClient from '../../../../../../jumbo/ZTrackingClient';
import CreateProgramModalContext from '../../../CreateProgramModalContext';

const AddEmployeeToProgram = ({
  subTitle,
  programId,
  showErrorToast,
  selectedWorkspace,
  processId,
  uploadEmployees,
  uploadingEmployees,
  employeesUploaded,
  setFileUploadMsg,
  uploadMsg,
  clearUpload,
  reviewProgramToEmployeesMapping,
  reviewingMapping,
  reviewedMapping,
}) => {
  const { goToPrevStep, stepsVisibility, goToStep } = useContext(
    FormCacheContext,
  );
  const { setModalSubtitle } = useContext(CreateProgramModalContext);
  const [emailList, setEmailList] = useState([]);
  const [file, setFile] = useState(null);
  const [uploadStatus, setUploadStatus] = useState('');
  const [uploadData, setUploadData] = useState([]);
  const [isPollingStarted, setIsPollingStarted] = useState(false);
  const [isUploadCompleted, setIsUploadCompleted] = useState(false);
  const [isUploadClicked, setIsUploadClicked] = useState(false);
  const [pollingFrequency, setPollingFrequency] = useState(2);
  const hasAdvancedRef = useRef(false);
  const [mappingFlag, setMappingFlag] = useState(false);
  const url = UPLOAD_EMPLOYEES_URL;
  const showProgressBar = uploadStatus === '' || uploadStatus === 'completed';
  let successful = '';
  let unsuccessful = '';
  let unsuccessfulLink = '';

  const handleClearFileClick = () => {
    setIsUploadCompleted(false);
    setIsPollingStarted(false);
    setFile(null);
  };

  const addEmailList = val => {
    if (validateEmail(val)) {
      setEmailList([...emailList, val]);
    }
  };

  useEffect(() => {
    setModalSubtitle(subTitle);
  }, []);

  const removeEmailList = val => {
    const updatedEmailList = [...emailList];
    updatedEmailList.splice(val, 1);
    setEmailList(updatedEmailList);
    setIsUploadCompleted(false);
  };

  const emailCheck = val => {
    return validateEmail(val);
  };

  const handleBackBtnClick = () => {
    stepsVisibility(true);
    ZTrackingClient.recordClick({
      ui_source: 'SEND_EGIFT_CARD_MODAL',
      component: 'GO_BACK_BUTTON',
    });
    goToPrevStep();
  };

  useEffect(() => {
    if (employeesUploaded) {
      setIsPollingStarted(true);
    }
  }, [uploadingEmployees, employeesUploaded]);

  useEffect(() => {
    if (uploadStatus === 'completed') {
      setIsPollingStarted(false);
      clearUpload();
    } else if (uploadStatus === 'failed') {
      clearUpload();
    }
  }, [uploadStatus]);

  // When the upload completes with zero failures, kick off the mapping review
  // exactly once. (Previously this was triggered inline during render, which
  // could fire on multiple re-renders and overshoot past the last step.)
  // onContinueClick reviews the program -> employee mapping; the effect below
  // moves to the confirm step once that review settles -- same pattern as the
  // Client POC path in Step 1.
  useEffect(() => {
    if (uploadStatus !== 'completed' || isPollingStarted) return;
    let unsuccessfulTokens = '';
    uploadData.forEach(uploadInfo => {
      if (uploadInfo.title === 'Unsuccessful Uploads')
        unsuccessfulTokens = (uploadInfo.subtitle ?? '').split(' ');
    });
    if (unsuccessfulTokens[0] === '0' && !hasAdvancedRef.current) {
      hasAdvancedRef.current = true;
      onContinueClick();
    }
  }, [uploadStatus, isPollingStarted, uploadData]);

  // Advance to the confirm/review step (Step 3) only after the mapping review
  // settles, mirroring the Client POC flow. goToStep(3) is absolute, so it can
  // never overshoot regardless of how many times the review is triggered.
  useEffect(() => {
    if (mappingFlag && !reviewingMapping) {
      setMappingFlag(false);
      if (reviewedMapping) goToStep(3);
    }
  }, [reviewingMapping]);

  const TopScroll = () => {
    const successRef = useRef();
    useEffect(() => { window.scrollTo({ top: 0, behavior: 'smooth' }); });
    return <div ref={successRef} />;
  };

  const onResponse = ({ isError, response }) => {
    let message = '';
    const status = _get(response, 'data.status', '');
    const uploads = _get(response, 'data.uploads', []);
    setUploadStatus(status);
    setUploadData(uploads ?? []);
    if (uploadSuccessStatus.includes(status)) {
      message = _get(response, 'data.message.title', groupUploadMsg);
    } else {
      message = _get(response, 'data.message.title', genericErrorMsg);
    }
    setFileUploadMsg(message);
    setIsUploadCompleted(true);
    if (isError) {
      setIsPollingStarted(false);
      setFileUploadMsg(groupUploadErrMsg);
      setUploadStatus('failed');
      return;
    }
    if (uploadSuccessStatus.includes(status) && !isError) {
      setPollingFrequency(3);
    } else {
      setIsPollingStarted(false);
    }
  };

  usePolling({
    processId,
    frequency: pollingFrequency,
    isPollingStarted,
    onResponse,
    url,
  });

  const onChange = e => {
    checkFileTypeSize(e.target.files[0])
      .then(success => {
        if (success) {
          setFile(e.target.files[0]);
        } else {
          showErrorToast(wrongFormat);
          clearInputValue();
        }
      })
      .catch(err => {
        clearInputValue();
        showErrorToast();
        console.log(err);
      });
  };

  const appendHeaderAndconvertArrayToCSV = emails => {
    const FinalEmails = ['emailid', ...emails];
    const csvContent = FinalEmails.map(row => `"${row}"`).join('\n');
    return csvContent;
  };

  const goNextForEmailChips = () => {
    setIsUploadClicked(true);
    const csvData = appendHeaderAndconvertArrayToCSV(emailList);
    const blob = new Blob([csvData], { type: 'text/csv' });
    const fileBody = new FormData();
    fileBody.append('file', blob);
    fileBody.append('workspace_id', selectedWorkspace.workspace_id);
    fileBody.append('program_id', programId);
    fileBody.append('flow_type', currentFlowTypeEnum.FLOW_TYPE_GIFT_CARD);
    uploadEmployees({
      fileBody,
    });
    ZTrackingClient.recordClick({
      ui_source: 'SEND_EGIFT_CARD_MODAL',
      component: 'UPLOAD_RECIPIENTS_BUTTON',
    });
  };

  const goNext = () => {
    setIsUploadClicked(true);
    const fileBody = new FormData();

    fileBody.append('file', file);
    fileBody.append('workspace_id', selectedWorkspace.workspace_id);
    fileBody.append('program_id', programId);
    fileBody.append('flow_type', currentFlowTypeEnum.FLOW_TYPE_GIFT_CARD);

    uploadEmployees({
      fileBody,
    });
    ZTrackingClient.recordClick({
      ui_source: 'SEND_EGIFT_CARD_MODAL',
      component: 'UPLOAD_RECIPIENTS_BUTTON',
    });
  };

  const onContinueClick = () => {
    const reviewParams = {
      workspace_id: selectedWorkspace?.workspace_id,
      program_id: programId,
    };
    // Review the program -> employee mapping, then let the effect watching
    // reviewingMapping move us to the confirm step once it resolves (same as
    // the Client POC path). Do NOT navigate eagerly here.
    setMappingFlag(true);
    reviewProgramToEmployeesMapping({
      reviewParams,
    });
    ZTrackingClient.recordClick({
      ui_source: 'SEND_EGIFT_CARD_MODAL',
      component: 'REVIEW_DETAILS_BUTTON',
    });
  };

  const disabled = file !== null || emailList.length !== 0;

  const fileUploadDisabled = emailList.length !== 0;
  const emailListDisabled = file;

  return (
    <ModalFieldsWrapper>
      <ConfirmUpload
        status={uploadStatus}
        progress={isPollingStarted}
        show={!showProgressBar || isPollingStarted || uploadingEmployees}
      >
        {uploadMsg}
      </ConfirmUpload>
      {(!showProgressBar || isPollingStarted || uploadingEmployees) && (
        <div>
          <TopScroll />
        </div>
      )}
      {uploadStatus === 'completed' && !isPollingStarted && (
        <UploadsContainer>
          {uploadData.forEach(uploadInfo => {
            const { title = '', subtitle = '', link = '' } = uploadInfo;
            if (title === 'Successful Uploads')
              successful = subtitle.split(' ');
            else if (title === 'Unsuccessful Uploads') {
              unsuccessful = subtitle.split(' ');
            }
            if (unsuccessful[0] !== '0') {
              unsuccessfulLink = link;
            }
          })}
          <ModalSuccessWrapper>
            {unsuccessful[0] !== '0' ? (
              <CreateGroupModalFooter unsuccessful={unsuccessful[0] !== '0'}>
                <UnsuccessfulUploadText>
                  <H5>
                    {unsuccessful[0]}
                    {' '}
                    {unsuccessful[0] === '0' || unsuccessful[0] === '1'
                      ? 'employee'
                      : 'employees'}
                    {' '}
                    could not be added
                  </H5>
                  <H6>Download file to see why </H6>
                </UnsuccessfulUploadText>
                <UnsuccessfulDownloadText
                  href={unsuccessfulLink}
                  target='_blank'
                  rel='noreferrer'
                >
                  <UnsuccessfulDownloadBtn>
                    Download
                    <UploadIconWhite>
                      <svg
                        xmlns='http://www.w3.org/2000/svg'
                        width='16'
                        height='16'
                        viewBox='0 0 16 16'
                        fill='none'
                      >
                        <path
                          d='M2 12.6654H14V13.9987H2V12.6654ZM8.66667 8.77977L12.7141 4.73236L13.6569 5.67518L8 11.332L2.34315 5.67518L3.28595 4.73236L7.33333 8.77977V1.33203H8.66667V8.77977Z'
                          fill='#414A5D'
                        />
                      </svg>
                    </UploadIconWhite>
                  </UnsuccessfulDownloadBtn>
                </UnsuccessfulDownloadText>
                {/* continue anyway */}
              </CreateGroupModalFooter>
            ) : (
              <CreateGroupModalFooter unsuccessful={unsuccessful[0] !== '0'}>
                <UnsuccessfulUploadText>
                  <H5>
                    {successful[0]}
                    {' '}
                    {successful[0] === '0' || successful[0] === '1'
                      ? 'employee'
                      : 'employees'}
                    {' '}
                    added successfully
                  </H5>
                </UnsuccessfulUploadText>
              </CreateGroupModalFooter>
            )}
          </ModalSuccessWrapper>
        </UploadsContainer>
      )}
      <UploadSectionContainer>
        <UploadFileWrapper
          isToast={isPollingStarted || uploadStatus === 'completed'}
          isAddEmployee
        >
          <Layout>
            <Layout.Item colD={12} colT={12} colM={12}>
              <div style={{ margin: '10px 0px 0px 10px' }}>
                <ChipsWrapper
                  activeColors={chipsStyle}
                  inertColors={chipsStyle}
                  label='Add recipients email ID(s)'
                  useBackSpace
                  list={emailList}
                  onAdd={addEmailList}
                  onRemove={removeEmailList}
                  helperText='  Use comma or space to add multiple email IDs'
                  errorText='Please enter valid email ID'
                  validator={emailCheck}
                  disabled={
                    emailListDisabled ||
                    isPollingStarted ||
                    uploadStatus === 'completed'
                  }
                  maxWidth='705px'
                />
              </div>
            </Layout.Item>
            <Layout.Item colD={6} colT={6} colM={6}>
              {' '}
            </Layout.Item>
            <Layout.Item colD={6} colT={6} colM={6}>
              <H6>OR</H6>
            </Layout.Item>
            <Layout.Item colD={12} colT={12} colM={12}>
              <ButtonWrapper>
                <label
                  htmlFor='upload-file'
                  style={
                    fileUploadDisabled || file
                      ? disabledUploadBtnStyle
                      : uploadBtnStyle
                  }
                >
                  <input
                    style={{
                      display: 'inline-block',
                      width: '100%',
                      padding: '120px 0 0 0',
                      height: '100px',
                      overflow: 'hidden',
                      zIndex: 2,
                      cursor:
                        !fileUploadDisabled && !file ? 'pointer' : 'default',
                    }}
                    id='upload-file'
                    type='file'
                    accept='.csv'
                    onChange={onChange}
                    disabled={fileUploadDisabled || file}
                  />
                  <H6
                    style={
                      fileUploadDisabled || file
                        ? {
                            color: '#9197A6',
                            cursor: 'default',
                            ...uploadFileText,
                          }
                        : {
                            color: '#EF4F5F',
                            cursor: 'pointer',
                            ...uploadFileText,
                          }
                    }
                  >
                    Upload recipients list
                  </H6>
                </label>
              </ButtonWrapper>
            </Layout.Item>
            <Layout.Item colD={8.5} colT={7.5} colM={7.5}>
              <TextWrapper>
                {file != null && (
                  <div>
                    <P> 1 File selected </P>
                    <ClearFileUploadCrossWrapper>
                      <FileSVG />
                      <H6 style={{ paddingLeft: '5px', marginTop: '-8px' }}>
                        {' '}
                        {file.name}
                      </H6>
                      <Button
                        appearance='outline'
                        style={clearFileBtnStyle}
                        onClick={handleClearFileClick}
                        icon={<ClearFileUploadCross />}
                        disabled={isPollingStarted}
                      />
                    </ClearFileUploadCrossWrapper>
                  </div>
                )}
                <P> &#x2022; Only .csv/.xls file upto 5MB supported</P>
              </TextWrapper>
            </Layout.Item>
            <Layout.Item colD={3.5} colT={7.5} colM={7.5}>
              <Button
                appearance='outline'
                style={downloadBtnStyle}
                onClick={downloadGroupCSV}
                icon={<Download />}
                btnColor={grey.z800}
                iconAlign='right'
              >
                <span style={{ color: grey.z600 }}>Download sample file</span>
              </Button>
            </Layout.Item>
          </Layout>
        </UploadFileWrapper>
        <CreateButtonWrapper>
          <Button
            appearance='outline'
            icon={<ChevronLeft />}
            disabled={isPollingStarted || isUploadClicked}
            iconAlign='left'
            btnColor='red'
            onClick={handleBackBtnClick}
          >
            Go Back
          </Button>
          {isUploadCompleted ? (
            <Button
              onClick={onContinueClick}
              loading={
                uploadingEmployees || isPollingStarted || reviewingMapping
              }
              icon={<ChevronRight />}
              iconAlign='right'
            >
              Review details
            </Button>
          ) : (
            <Button
              onClick={file != null ? goNext : goNextForEmailChips}
              loading={
                uploadingEmployees || isPollingStarted || reviewingMapping
              }
              disabled={!disabled}
              icon={<ChevronRight />}
              iconAlign='right'
            >
              Next
            </Button>
          )}
        </CreateButtonWrapper>
      </UploadSectionContainer>
    </ModalFieldsWrapper>
  );
};

AddEmployeeToProgram.propTypes = {
  subTitle: PropTypes.string,
  programId: PropTypes.string,
  showErrorToast: PropTypes.func,
  selectedWorkspace: PropTypes.objectOf(PropTypes.any),
  processId: PropTypes.string,
  uploadEmployees: PropTypes.func,
  uploadingEmployees: PropTypes.bool,
  employeesUploaded: PropTypes.bool,
  setFileUploadMsg: PropTypes.func,
  uploadMsg: PropTypes.string,
  clearUpload: PropTypes.func,
  reviewProgramToEmployeesMapping: PropTypes.func,
  reviewingMapping: PropTypes.bool,
  reviewedMapping: PropTypes.bool,
};

AddEmployeeToProgram.defaultProps = {
  subTitle: '',
  programId: '',
  showErrorToast: _noop,
  selectedWorkspace: {},
  processId: '',
  uploadEmployees: _noop,
  uploadingEmployees: false,
  employeesUploaded: false,
  setFileUploadMsg: _noop,
  uploadMsg: '',
  clearUpload: _noop,
  reviewProgramToEmployeesMapping: _noop,
  reviewingMapping: false,
  reviewedMapping: false,
};

export default AddEmployeeToProgram;
